'use client';

import React, { useEffect, useState } from 'react';
import { Block, BLOCKS, Document, TopLevelBlock, TOP_LEVEL_BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { useBreakpoint } from '@/hooks/useBreakpoints';
import { cn } from 'utils/styles';

function hasContent(block: Block): block is Block & { content: Block[] } {
  return Array.isArray((block as Block).content);
}

export function blockToReactComponents(block: Block, options?: Options) {
  const isTopLevel = TOP_LEVEL_BLOCKS.includes(block.nodeType as (typeof TOP_LEVEL_BLOCKS)[number]);
  const isTableCell = block.nodeType === 'table-cell' || block.nodeType === 'table-header-cell';

  if (isTableCell && hasContent(block)) {
    const doc: Document = {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: block.content as TopLevelBlock[],
    };
    return documentToReactComponents(doc, options);
  }

  const doc: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: isTopLevel
      ? [block as TopLevelBlock]
      : [
          {
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: hasContent(block) ? block.content : [],
          } as TopLevelBlock,
        ],
  };
  return documentToReactComponents(doc, options);
}

function TableCell({ cell, className, options }: { cell?: Block; className?: string; options?: Options }) {
  // A data row may have fewer cells than the header row when Contentful columns are
  // unevenly populated. Render an empty <td> to keep the table layout intact.
  if (!cell) {
    return <td className={className} />;
  }
  const content = blockToReactComponents(cell, options);
  if (cell.nodeType === 'table-header-cell') {
    return <th className={cn('font-sans font-semibold py-6', className)}>{content}</th>;
  }
  return <td className={cn('py-6 text-(--table-text-color)', className)}>{content}</td>;
}

interface ResponsiveTableProps {
  node: Block;
  children: React.ReactNode;
  renderOptions?: Options;
}

const ResponsiveTable = ({ node, children, renderOptions }: ResponsiveTableProps) => {
  const { isAboveMd } = useBreakpoint('md');
  const [hasMounted, setHasMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- React-recommended two-pass hydration pattern, see https://react.dev/reference/react-dom/client/hydrateRoot#handling-different-client-and-server-content
  useEffect(() => setHasMounted(true), []);

  // First render (SSR + first client side paint): always show desktop layout to avoid server/client mismatch warnings.
  // After mount: use the real breakpoint value.
  const showDesktopLayout = !hasMounted || isAboveMd;

  if (showDesktopLayout) {
    return <table className="w-full text-left">{children}</table>;
  }

  const rows = (node.content ?? []) as Block[];
  const headerRow = (rows[0]?.content ?? []) as Block[];
  const dataRows = rows.slice(1);

  if (headerRow.length === 0) {
    return null;
  }

  // Single-row table: render each cell as its own row vertically
  if (dataRows.length === 0) {
    return (
      <table className="w-full text-left">
        <tbody>
          {headerRow.map((cell, idx) => (
            <tr key={idx} className="border-b border-pebble">
              <TableCell cell={cell} options={renderOptions} />
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {dataRows.map((dataRow, tableIdx) => {
        const dataCells = (dataRow.content ?? []) as Block[];
        return (
          <table key={tableIdx} className="w-full table-fixed text-left">
            <tbody>
              {headerRow.map((headerCell, rowIdx) => (
                <tr key={rowIdx} className="border-b border-pebble">
                  <TableCell cell={headerCell} className="w-1/2 wrap-break-word" options={renderOptions} />
                  <TableCell cell={dataCells[rowIdx]} className="w-1/2 wrap-break-word" options={renderOptions} />
                </tr>
              ))}
            </tbody>
          </table>
        );
      })}
    </div>
  );
};

export default ResponsiveTable;
